import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import { rentalService } from '../../services/rental.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import { PlusIcon, TrashIcon, HomeIcon } from '../../components/icons/Icons';

const indigo = {
  50:  '#eef2ff',
  100: '#e0e7ff',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
};

const RentalList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRentals(); }, []);

  const fetchRentals = async () => {
    try {
      const res = await rentalService.myListings();
      setRentals(res.data?.rentals || []);
    } catch {
      toast.error('Failed to load rental listings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await rentalService.delete(id);
      toast.success('Listing deleted');
      setRentals((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error('Failed to delete listing');
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  return (
    <MainLayout>
      <div style={{ padding: '32px 24px', maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '13px',
              background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HomeIcon size={24} color="#fff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e1b4b' }}>My Rentals</h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{rentals.length} listing{rentals.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/seller/rentals/create')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '11px 22px', border: 'none', borderRadius: '10px',
              background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
              color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
              fontFamily: 'inherit', boxShadow: `0 4px 14px ${indigo[100]}`,
            }}
          >
            <PlusIcon size={16} /> New Listing
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>Loading…</div>
        ) : rentals.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 32px',
            background: '#fff', borderRadius: '16px',
            border: '1.5px dashed #e0e7ff',
          }}>
            <div style={{ fontSize: '52px', marginBottom: '12px' }}>🏠</div>
            <h3 style={{ margin: 0, color: '#1e1b4b', fontWeight: '700' }}>No rental listings yet</h3>
            <p style={{ color: '#6b7280', marginTop: '6px', fontSize: '14px' }}>
              List your first property and start receiving enquiries.
            </p>
            <button
              onClick={() => navigate('/seller/rentals/create')}
              style={{
                marginTop: '20px', padding: '11px 28px', border: 'none',
                borderRadius: '10px', background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
                color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + List Property
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {rentals.map((r) => (
              <div
                key={r.id}
                style={{
                  background: '#fff', borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 4px 20px rgba(99,102,241,.07)',
                  border: '1px solid #e8eaf6', overflow: 'hidden',
                  transition: 'transform .2s, box-shadow .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(99,102,241,.14)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.06), 0 4px 20px rgba(99,102,241,.07)';
                }}
              >
                {/* Image */}
                <div style={{ height: '180px', background: '#f0f1ff', overflow: 'hidden', position: 'relative' }}>
                  {r.images?.[0] ? (
                    <img src={r.images[0]} alt={r.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '40px' }}>🏠</div>
                  )}
                  <span style={{
                    position: 'absolute', top: '10px', right: '10px',
                    background: r.is_active ? '#dcfce7' : '#fef2f2',
                    color: r.is_active ? '#16a34a' : '#dc2626',
                    fontSize: '11px', fontWeight: '700', borderRadius: '6px', padding: '3px 8px',
                  }}>
                    {r.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Info */}
                <div style={{ padding: '18px' }}>
                  <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: '#1e1b4b', lineHeight: '1.3' }}>{r.title}</h3>
                  <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#6b7280', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {r.description || 'No description provided'}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.4px' }}>Rent/month</div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: indigo[600] }}>{fmt(r.rent_per_month)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.4px' }}>Vacancies</div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e1b4b' }}>{r.vacancies}</div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(r.id, r.title)}
                    style={{
                      width: '100%', padding: '9px', border: '1.5px solid #fee2e2',
                      borderRadius: '8px', background: '#fff5f5', color: '#dc2626',
                      fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                      fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '6px', transition: 'background .2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff5f5'}
                  >
                    <TrashIcon size={14} /> Delete Listing
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RentalList;
